'use client'

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { NextIntlClientProvider } from 'next-intl';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { I18nContextProvider } from '@/contexts/I18nContext';
import { UnifiedCartProvider } from '@/lib/unified-cart';
import { ActivityCartProvider } from '@/lib/activity-cart';
import UnifiedFloatingCart from '@/components/booking/UnifiedFloatingCart';
import type { Locale } from '@/i18n/routing';

// Import messages statically for all locales
import enMessages from '@/messages/en.json';
import esMessages from '@/messages/es.json';
import ptMessages from '@/messages/pt.json';

const messages = {
  en: enMessages,
  es: esMessages,
  pt: ptMessages,
};

/**
 * Client-side layout component for booking routes
 * Handles locale detection from cookies and provides i18n context
 * NOTE: html/body tags are provided by the root layout (app/layout.tsx)
 *
 * OPTIMIZED: Shows skeleton immediately instead of blank screen,
 * then renders full content once locale is detected
 */
export default function BookingLayoutClient({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const isCheckout = pathname.includes('/checkout');
  const [locale, setLocale] = useState<Locale>('en');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Detect locale from cookie on client-side
    const cookieLocale = document.cookie
      .split('; ')
      .find(row => row.startsWith('NEXT_LOCALE='))
      ?.split('=')[1] as Locale | undefined;

    if (cookieLocale && (cookieLocale === 'en' || cookieLocale === 'es' || cookieLocale === 'pt')) {
      setLocale(cookieLocale);
    }

    setMounted(true);
  }, []);

  // OPTIMIZED: Show skeleton structure instead of blank screen
  // This preserves layout and reduces CLS (Cumulative Layout Shift)
  if (!mounted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0a0a0a' }}>
        {/* Skeleton header */}
        <div style={{ height: '80px', backgroundColor: '#111' }} />
        {/* Skeleton content area */}
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ height: '200px', backgroundColor: '#1a1a1a', borderRadius: '8px', marginBottom: '1rem' }} />
          <div style={{ height: '400px', backgroundColor: '#1a1a1a', borderRadius: '8px' }} />
        </div>
      </div>
    );
  }

  return (
    <NextIntlClientProvider messages={messages[locale]} locale={locale}>
      <I18nContextProvider>
        <UnifiedCartProvider>
          <ActivityCartProvider>
            <Header />
            <main>{children}</main>
            {!isCheckout && <Footer />}
            <UnifiedFloatingCart />
          </ActivityCartProvider>
        </UnifiedCartProvider>
      </I18nContextProvider>
    </NextIntlClientProvider>
  );
}
